import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import {
  tryCreate,
  tryDelete,
  tryGet,
  tryList,
  tryUpdate,
} from "./_shared";

export type ExternalSystem = Tables<"external_systems">;
export type ExternalSystemInsert = Omit<TablesInsert<"external_systems">, "company_id">;
export type ExternalSystemUpdate = TablesUpdate<"external_systems">;

type ExternalSystemWithAudit = ExternalSystem & {
  created_by_profile: { full_name: string | null } | null;
  updated_by_profile: { full_name: string | null } | null;
};

export interface UsageCounts {
  vendors: { total: number };
  price_books: { total: number };
  price_book_items: { active: number; inactive: number };
  invoices: { total: number };
  invoice_items: { total: number };
  tickets: { total: number };
  ticket_items: { total: number };
}

export const externalSystemService = {
  get(id: string) {
    return tryGet<ExternalSystem>(`/api/external-systems/${encodeURIComponent(id)}`);
  },
  getWithAuditNames(id: string) {
    return tryGet<ExternalSystemWithAudit>(
      `/api/external-systems/${encodeURIComponent(id)}?withAudit=true`,
    );
  },
  getByName(name: string, _companyId: string) {
    return tryGet<ExternalSystem>(`/api/external-systems/by-name/${encodeURIComponent(name)}`);
  },
  list(_companyId: string) {
    return tryList<ExternalSystem>("/api/external-systems");
  },
  create(data: ExternalSystemInsert, _context: ServiceContext) {
    return tryCreate<ExternalSystem>("/api/external-systems", data);
  },
  update(
    id: string,
    data: ExternalSystemUpdate,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ) {
    return tryUpdate<ExternalSystem>(`/api/external-systems/${encodeURIComponent(id)}`, data);
  },
  delete(id: string, _snapshot: Record<string, unknown>, _context: ServiceContext) {
    return tryDelete(`/api/external-systems/${encodeURIComponent(id)}`);
  },
  async getUsageCounts(externalSystemName: string, _companyId: string) {
    return tryGet<UsageCounts>(
      `/api/external-systems/by-name/${encodeURIComponent(externalSystemName)}/usage`,
    );
  },
  async isInUse(externalSystemName: string, companyId: string) {
    const result = await externalSystemService.getUsageCounts(externalSystemName, companyId);
    if (result.error || !result.data) return { data: false, error: result.error };
    const c = result.data;
    const total =
      c.vendors.total +
      c.price_books.total +
      c.price_book_items.active +
      c.price_book_items.inactive +
      c.invoices.total +
      c.invoice_items.total +
      c.tickets.total +
      c.ticket_items.total;
    return { data: total > 0, error: null };
  },
  getTotalAffectedRecords(counts: UsageCounts): number {
    return (
      counts.vendors.total +
      counts.price_books.total +
      counts.price_book_items.active +
      counts.price_book_items.inactive +
      counts.invoices.total +
      counts.invoice_items.total +
      counts.tickets.total +
      counts.ticket_items.total
    );
  },
};
